import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import dateFormat from "dateformat";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import { apiError } from "../../../helpers/ErrorHelper";
import { useAuth } from "../../../context/AuthContext";
import { createCreditNote } from "../../../models/CreditNoteModel";
import { getInvoices, getInvoiceDetails } from "../../../models/InvoiceModel";
import { getData } from "../../../models/ClientsModel";
import { getData as getClientBranches } from "../../../models/ClientBranchesModel";
import Select from "react-select";
import { useTheme } from "../../../context/ThemeContext";
import { useNotificationCount } from "../../../context/NotificationCountContext";

const matchClientId = (a, b) => String(a ?? "") === String(b ?? "");

const isValidDate = (value) =>
  value instanceof Date && !Number.isNaN(value.getTime());

const parseBillDate = (value) => {
  if (!value) {
    return null;
  }
  const raw = String(value).trim();
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    return isValidDate(d) ? d : null;
  }
  const parsed = new Date(raw);
  return isValidDate(parsed) ? parsed : null;
};

/** Invoice month is usually MM-YYYY in DB; may also be Sep-2026 from display. */
const parseInvoiceMonthDate = (monthStr) => {
  if (!monthStr) {
    return null;
  }
  const raw = String(monthStr).trim();
  const parts = raw.split(/[-/]/);
  if (parts.length === 2) {
    const a = parts[0].trim();
    const b = parts[1].trim();
    if (/^\d{4}$/.test(a) && /^\d{1,2}$/.test(b)) {
      const d = new Date(Number(a), Number(b) - 1, 1);
      return isValidDate(d) ? d : null;
    }
    if (/^\d{1,2}$/.test(a) && /^\d{4}$/.test(b)) {
      const d = new Date(Number(b), Number(a) - 1, 1);
      return isValidDate(d) ? d : null;
    }
    const fromText = new Date(Date.parse(`1 ${a} ${b}`));
    return isValidDate(fromText) ? fromText : null;
  }
  const parsed = new Date(raw);
  return isValidDate(parsed) ? parsed : null;
};

const safeFormatInvoiceLabelDate = (value) => {
  const d = parseBillDate(value);
  if (!d) {
    return "—";
  }
  try {
    return dateFormat(d, "dd mmm yyyy");
  } catch {
    return "—";
  }
};

const CreateCreditNoteModal = ({
  showModal,
  setShowModal,
  fetchCreditNotes,
  limit,
  page,
  _client_id,
}) => {
  const { setNotificationCount } = useNotificationCount();
  const { selectBoxTheme } = useTheme();
  const navigate = useNavigate();
  const { state } = useAuth();
  const user_data = state.user_data;
  const loader = document.getElementById("loading");

  const [loading, setLoading] = useState(false);
  const [credit_note_client_id, setCreditNoteClientID] = useState("");
  const [credit_note_branch_id, setCreditNoteBranchID] = useState("");
  const [branchOptions, setBranchOptions] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [credit_note_bill_date, setCreditNoteBillDate] = useState(null);
  const [credit_note_month, setCreditNoteMonth] = useState(null);
  const [credit_note_po_number, setCreditNotePONumber] = useState("");
  const [invoice_link, setInvoiceLink] = useState("");
  const [invoiceOptions, setInvoiceOptions] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [clients, setClients] = useState([]);

  const closeModal = () => {
    setCreditNoteClientID("");
    setCreditNoteBranchID("");
    setBranchOptions([]);
    setCreditNoteBillDate(null);
    setCreditNoteMonth(null);
    setCreditNotePONumber("");
    setInvoiceLink("");
    setInvoiceOptions([]);
    setErrors({});
    setShowModal(false);
  };

  const loadBranchesForClient = async (clientId, { autoCreate = true } = {}) => {
    if (!clientId) {
      setBranchOptions([]);
      setCreditNoteBranchID("");
      return [];
    }
    setBranchesLoading(true);
    try {
      const response = await getClientBranches({
        user_token: user_data.user_token,
        client_branch_client_id: clientId,
        limit: -1,
        ...(autoCreate ? { auto_create: "1" } : {}),
      });
      const rows = response?.client_branches || [];
      const options = rows.map((b) => ({
        value: b._client_branch_id,
        label: [
          b.client_branch_name,
          b.client_branch_gstin ? `(${b.client_branch_gstin})` : null,
          Number(b.client_branch_is_default) === 1 ? "— Default" : null,
        ]
          .filter(Boolean)
          .join(" "),
        isDefault: Number(b.client_branch_is_default) === 1,
      }));
      setBranchOptions(options);
      const defaultOpt =
        options.find((o) => o.isDefault) || (options.length === 1 ? options[0] : null);
      setCreditNoteBranchID(defaultOpt || "");
      return options;
    } catch (error) {
      setBranchOptions([]);
      setCreditNoteBranchID("");
      return [];
    } finally {
      setBranchesLoading(false);
    }
  };

  const loadInvoicesForClient = async (clientId) => {
    if (!clientId) {
      setInvoiceOptions([]);
      setInvoiceLink("");
      return;
    }
    setInvoicesLoading(true);
    try {
      const response = await getInvoices({
        user_token: user_data.user_token,
        page: 1,
        limit: 500,
        _client_id: clientId,
        exclude_with_credit_note: 1,
      });
      const rows = response?.invoices || [];
      setInvoiceOptions(
        rows.map((inv) => ({
          value: inv.invoice_unique_id,
          label: `${inv.invoice_number} (${safeFormatInvoiceLabelDate(inv.invoice_bill_date)})`,
        }))
      );
    } catch (error) {
      setInvoiceOptions([]);
    } finally {
      setInvoicesLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const result = await getData({ user_token: user_data.user_token, page: 1, limit: 500 });
      const clientList = result?.clients || [];
      const clientOptions = clientList.map((client) => ({
        label: `${client.client_name} (${client.client_gstin || "No GSTIN"})`,
        value: client._client_id,
      }));
      setClients(clientOptions);

      if (_client_id) {
        const selectedClient = clientList.find((client) =>
          matchClientId(client._client_id, _client_id)
        );
        if (selectedClient) {
          setCreditNoteClientID({
            label: selectedClient.client_name,
            value: selectedClient._client_id,
          });
          await loadBranchesForClient(selectedClient._client_id);
          await loadInvoicesForClient(selectedClient._client_id);
        }
      }
      setNotificationCount(localStorage.getItem("unread_notification_count"));
    } catch (error) {
      // ignore
    }
  };

  const clientChange = async (clientObj) => {
    setCreditNoteClientID(clientObj || "");
    const clientId = clientObj?.value || "";
    setInvoiceLink("");
    await loadBranchesForClient(clientId);
    await loadInvoicesForClient(clientId);
  };

  const applyInvoiceToForm = async (invoiceOption) => {
    setInvoiceLink(invoiceOption || "");
    if (!invoiceOption?.value) {
      return;
    }
    try {
      const response = await getInvoiceDetails({
        user_token: user_data.user_token,
        invoice_unique_id: invoiceOption.value,
      });
      if (response?.status !== true) {
        return;
      }
      const inv = response.data?.invoice_data;
      if (!inv) {
        return;
      }
      const bill = parseBillDate(inv.invoice_bill_date);
      if (bill) {
        setCreditNoteBillDate(bill);
      }
      const monthDate = parseInvoiceMonthDate(inv.invoice_month);
      if (monthDate) {
        setCreditNoteMonth(monthDate);
      }
      if (inv.invoice_po_number) {
        setCreditNotePONumber(inv.invoice_po_number);
      }
      if (inv.invoice_branch_id) {
        let branches = branchOptions;
        if (!branches.length && credit_note_client_id?.value) {
          branches = await loadBranchesForClient(credit_note_client_id.value, {
            autoCreate: false,
          });
        }
        const branchMatch = branches.find(
          (o) => String(o.value) === String(inv.invoice_branch_id)
        );
        if (branchMatch) {
          setCreditNoteBranchID(branchMatch);
        }
      }
    } catch (error) {
      apiError(error);
    }
  };

  useEffect(() => {
    if (showModal) {
      fetchClients();
    }
  }, [showModal, _client_id]);

  const addCreditNote = async () => {
    loader.style.display = "block";
    setLoading(true);
    try {
      setErrors({});
      const formData = {
        credit_note_client_id: credit_note_client_id?.value ? credit_note_client_id.value : "",
        credit_note_branch_id: credit_note_branch_id?.value ? credit_note_branch_id.value : "",
        credit_note_bill_date: credit_note_bill_date
          ? dateFormat(credit_note_bill_date, "dd-mm-yyyy")
          : "",
        credit_note_month: credit_note_month ? dateFormat(credit_note_month, "mmm-yyyy") : "",
        credit_note_po_number: credit_note_po_number || "",
        invoice_unique_id: invoice_link?.value ? invoice_link.value : "",
      };

      const response = await createCreditNote(user_data.user_token, formData);

      if (response.status) {
        toast.success(response.message);
        const creditNoteId = response.data?.credit_note_unique_id;
        closeModal();
        fetchCreditNotes(limit, page, _client_id);
        setNotificationCount(localStorage.getItem("unread_notification_count"));
        if (creditNoteId) {
          navigate(`/credit-notes/${creditNoteId}/edit`);
        }
      } else {
        setErrors(response.error || {});
        toast.error(response.message);
      }
    } catch (error) {
      apiError(error);
      if (error.response?.data?.invalid_token === 1) {
        navigate("/re-login");
      }
    }
    loader.style.display = "none";
    setLoading(false);
  };

  return (
    <Modal show={showModal} onHide={closeModal} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Create credit note</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row gy-2">
          <div className="col-xl-12">
            <label className="form-label">
              Client <span className="text-danger">*</span>
            </label>
            <Select
              className="form-control custom-select"
              value={credit_note_client_id}
              onChange={clientChange}
              options={clients}
              placeholder="- Client -"
              isSearchable
              styles={selectBoxTheme}
            />
            {errors.credit_note_client_id && (
              <p className="text-danger">{errors.credit_note_client_id}</p>
            )}
          </div>

          {credit_note_client_id?.value ? (
            <div className="col-xl-12">
              <label className="form-label">Billing branch</label>
              <Select
                className="form-control custom-select"
                value={credit_note_branch_id}
                onChange={setCreditNoteBranchID}
                options={branchOptions}
                placeholder={branchesLoading ? "Loading…" : "- Billing branch -"}
                isSearchable
                isClearable
                isLoading={branchesLoading}
                styles={selectBoxTheme}
              />
              {errors.credit_note_branch_id && (
                <p className="text-danger">{errors.credit_note_branch_id}</p>
              )}
            </div>
          ) : null}

          <div className="col-xl-12">
            <label className="form-label">Link to invoice (optional)</label>
            <Select
              className="form-control custom-select"
              value={invoice_link}
              onChange={applyInvoiceToForm}
              options={invoiceOptions}
              placeholder={
                !credit_note_client_id?.value
                  ? "Select a client first"
                  : invoicesLoading
                    ? "Loading invoices…"
                    : "— No linked invoice —"
              }
              isSearchable
              isClearable
              isDisabled={!credit_note_client_id?.value}
              isLoading={invoicesLoading}
              styles={selectBoxTheme}
            />
            <small className="text-muted">
              Optional. Only invoices without an existing credit note are listed. Choosing one
              copies its line items, shipping address, and totals into the new credit note.
            </small>
          </div>

          <div className="col-xl-6">
            <label className="form-label">
              Credit note date <span className="text-danger">*</span>
            </label>
            <DatePicker
              className="form-control"
              placeholderText="DD-MM-YYYY"
              selected={isValidDate(credit_note_bill_date) ? credit_note_bill_date : null}
              onChange={(date) => setCreditNoteBillDate(isValidDate(date) ? date : null)}
              dateFormat="dd-MM-yyyy"
            />
            {errors.credit_note_bill_date && (
              <p className="text-danger">{errors.credit_note_bill_date}</p>
            )}
          </div>

          <div className="col-xl-6">
            <label className="form-label">
              Credit note month <span className="text-danger">*</span>
            </label>
            <DatePicker
              className="form-control"
              placeholderText="MMM-YYYY"
              selected={isValidDate(credit_note_month) ? credit_note_month : null}
              showMonthYearPicker
              onChange={(date) => setCreditNoteMonth(isValidDate(date) ? date : null)}
              dateFormat="MMM-yyyy"
            />
            {errors.credit_note_month && (
              <p className="text-danger">{errors.credit_note_month}</p>
            )}
          </div>

          <div className="col-xl-6">
            <label className="form-label">PO number</label>
            <input
              type="text"
              className="form-control"
              value={credit_note_po_number}
              onChange={(e) => setCreditNotePONumber(e.target.value)}
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-light" onClick={closeModal}>
          Cancel
        </button>
        <button type="button" className="btn btn-primary" onClick={addCreditNote}>
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm align-middle"></span>
              Loading...
            </>
          ) : (
            "Submit"
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateCreditNoteModal;
